import React from 'react';
import { PAYMENTS } from 'state/payment/type';
import CURRENCY from 'utils/currency';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui/icon';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';

interface props {
  info: PAYMENTS.IGatewayTransaction;
}

interface PaymentRowProps {
  label: string;
  value: number;
  boldLabel?: boolean;
  boldValue?: boolean;
  tooltip?: string;
  note?: string;
  isDashed?: boolean;
  className?: string;
}

const PaymentRow: React.FC<PaymentRowProps> = ({
  label,
  value,
  boldLabel,
  boldValue,
  tooltip,
  note,
  isDashed,
  className = '',
}) => {
  return (
    <div className={cn('pb-1', className)}>
      <div
        className={cn(
          'flex items-center justify-between py-1 md:max-w-lg',
          isDashed && 'border-t border-dashed border-gray-blue-20 pt-1'
        )}
      >
        <div
          className={cn(
            'flex items-center pr-1',
            'text-gray-blue-50 text-sm leading-5 font-normal',
            boldLabel && 'font-medium'
          )}
        >
          {label}
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center pl-1 cursor-help">
                    <Icon
                      name="info"
                      size={16}
                      className="text-gray-blue-50"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <span
          className={cn(
            'text-gray-blue-70 text-sm leading-5 font-normal',
            boldValue && 'font-semibold'
          )}
        >
          {CURRENCY.convertToMainUnit(value)}
        </span>
      </div>
      {note && (
        <div className="text-xs italic font-normal text-gray-blue-60 leading-4">
          {note}
        </div>
      )}
    </div>
  );
};

const GatewayPaymentBreakDown = ({
  info = {} as PAYMENTS.IGatewayTransaction,
}: props): JSX.Element => {
  const grossAmount =
    info.original_amount + info.tip_amount + (info?.cashback || 0);

  return (
    <>
      <div className="mb-9">
        <h3 className="mb-2 text-xl leading-7 font-semibold text-gray-blue-80">
          Payment Breakdown
        </h3>
        <hr className="mt-0 mb-2 pb-1" />
        <div>
          <PaymentRow label="Order Amount" value={info.original_amount} />
          <PaymentRow label="Cashback Amount" value={info?.cashback || 0} />
          <PaymentRow label="Tip" value={info.tip_amount} />
          <PaymentRow
            label="Gross Amount"
            value={grossAmount}
            boldLabel
            boldValue
            isDashed
            className="pb-4"
          />
          <PaymentRow
            label="Convenience Fee"
            value={info.fee_amount}
            tooltip="This fee is applied to all transactions but is not added to the gross amount above."
          />
          <div className="pb-2 text-xs italic font-normal text-gray-blue-60 leading-4">
            (Not included in gross receipts)
          </div>
          <PaymentRow
            label="Total (+ fee)"
            value={grossAmount + info.fee_amount}
            isDashed
            className="pb-1 mb-2"
          />
        </div>
      </div>
    </>
  );
};

export default GatewayPaymentBreakDown;
